import { Fragment, useEffect, useMemo, useState } from "react";
import { BackToTopPill } from "@/components/BackToTopPill";
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from "react-router-dom";
import {
  AlertCircle,
  ArrowRightLeft,
  Boxes,
  CalendarIcon,
  ChevronDown,
  ChevronRight,
  ClipboardList,
  Download,
  ExternalLink,
  FileSpreadsheet,
  PackageCheck,
  RefreshCw,
  Search,
  Upload,
  Workflow,
  XCircle,
} from "lucide-react";
import { MetricCard } from "@/features/production-orders/components/metrics/MetricCard";
import { format as formatDate } from "date-fns";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toCsvContent } from "@/lib/csv";
import { downloadCsvFile } from "@/lib/download";
import BulkImportsTab from "@/features/inventory/components/bulk-import-history/BulkImportsTab";
import { ClientFilterDropdown } from "@/features/inventory/components/ClientFilterDropdown";
import { ALL_CLIENTS } from "@/features/inventory/hooks/useClientOrgFilter";
import { useConnectedClients } from "@/features/inventory/hooks/useConnectedClients";
import { hexSlice } from "@/features/inventory/utils/partnerFallback";
import ReceiveHistory from "./ReceiveHistory";
import { useCurrencyFormatter } from "@/hooks/useCurrencyFormatter";
import RoleGuard from "@/components/auth/RoleGuard";
import { PageLoader } from "@/components/loading/PageLoader";
import { useLocation } from "@/contexts/LocationContext";
import { useOrganization } from "@/contexts/OrganizationContext";
import { useLocations } from "@/features/locations/hooks/useLocations";
import { usePushHistory } from "@/features/stock-sync/hooks/useStockSync";
import { parsePushDetails } from "@/features/stock-sync/utils/parsePushDetails";
import { trpc } from "@/lib/trpc";
import { cn } from "@/lib/utils";
import { formatDistanceToNow } from "date-fns";
import { Badge } from "@/components/ui/badge";
import { StatusBadge } from "@/components/ui/status-badge";
import { EmptyState } from "@/components/shared/EmptyState";
import { PageErrorState } from "@/components/shared/PageErrorState";
import {
  type MatchStatus,
  type MovementType,
  type ProductionEventType,
  MOVEMENT_TYPE_LABELS,
  matchStatusStyle,
  movementTypeStyle,
  prodEventStatusStyle,
} from "@/features/logs/utils/status-style";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { usePageTitle } from "@/contexts/PageTitleContext";
import { PageActionBar } from "@/components/layout/PageActionBar";
import { formatSupplyPrice } from "@/features/inventory/utils/supplyAliasGroups";
import {
  backupCommitFilterLabel,
  backupCommitTypeLabel,
  movementNotesText,
  type MovementNotesRow,
} from "@/features/logs/utils/backup-commit-notes";

type MatchFilter = MatchStatus | "all" | "issues";
type MovementScope = "all" | "order_workflow" | "internal";
// BLU-589: derived push-outcome statuses for the shopify_push tab filter.
type PushStatusFilter = "success" | "partial" | "failed" | "no_changes";

const PAGE_SIZE = 50;

/** Format Date as YYYY-MM-DD in local time (avoids timezone shift from .toISOString()) */
const toLocalDateString = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

/** Centralised CSV download for every Logs tab export. Delegates to the
 *  canonical `downloadCsvFile`, which prepends the UTF-8 BOM — without it,
 *  Excel on Windows sniffs the file as ANSI and Chinese product titles
 *  arrive as mojibake (a direct problem for Thread Lab exporting logs). */
const downloadCsv = (rows: unknown[][], filename: string) => {
  downloadCsvFile(toCsvContent(rows), filename);
};

const SHARED_LOCATIONS_ENABLED =
  (import.meta.env.VITE_SHARED_LOCATIONS_ENABLED ?? "") === "true";

/** Reference types whose reference_id is the literal operator-facing value —
 *  safe targets for the Order/Ref click-to-filter (exact match). */
const CLICK_FILTERABLE_REFERENCE_TYPES = new Set([
  "adjustment_ref",
  "scrap",
  "po_number",
  "transfer_number",
]);

const EXPORT_ROW_CAP = 10_000;

// Orders tab window. The backend bounds the rows AND the exact count to this
// many days of `orders.created_at`, so the pager, the tab total and the CSV all
// describe the same set. Keep the options in step with the backend's max.
// 365 is that max, and it matches ORDER_SYNC_MAX_ORDER_AGE_DAYS: the cron
// re-ingests orders up to a year old on any fulfilment, refund or update, so a
// narrower window is the only thing standing between an operator and a repulled
// order they came here to check.
const ORDER_WINDOW_OPTIONS = [7, 30, 90, 365] as const;
const ORDER_WINDOW_DEFAULT_DAYS = 30;

const matchFilterOptions: Array<{ value: MatchFilter; label: string }> = [
  { value: "all", label: "All" },
  { value: "issues", label: "Issues Only" },
  { value: "all_matched", label: "All Matched" },
  { value: "partially_matched", label: "Partially Matched" },
  { value: "unmatched", label: "Unmatched" },
  { value: "no_items", label: "No Items" },
];

// BLU-589: labels mirror the derived status badges rendered in the push table.
const pushStatusOptions: Array<{ value: PushStatusFilter; label: string }> = [
  { value: "success", label: "Success" },
  { value: "partial", label: "Partial" },
  { value: "failed", label: "Failed" },
  { value: "no_changes", label: "No changes" },
];

// Explicit order (not object key order, which isn't guaranteed): Committed
// and Released sit right after Return so the order-flow family — Sale,
// Return, Committed, Released — stays contiguous (BLU-811).
const MOVEMENT_TYPE_FILTER_ORDER: MovementType[] = [
  "sale",
  "return",
  "committed",
  "released",
  "backup_commit",
  "purchase",
  "adjustment",
  "damage",
  "count",
  "pre_print",
  "substitution",
];

// Derived from the full label map (not just MOVEMENT_TYPE_FILTER_ORDER) so a
// new MovementType is never silently unfilterable — sorted by the explicit
// order above, with any key the order array doesn't know about appended at
// the end via Array.sort's stability.
const movementTypeOptions: Array<{ value: MovementType | "all"; label: string }> = [
  { value: "all", label: "All Types" },
  ...(Object.keys(MOVEMENT_TYPE_LABELS) as MovementType[])
    .sort((a, b) => {
      const aIndex = MOVEMENT_TYPE_FILTER_ORDER.indexOf(a);
      const bIndex = MOVEMENT_TYPE_FILTER_ORDER.indexOf(b);
      if (aIndex === -1 && bIndex === -1) return 0;
      if (aIndex === -1) return 1;
      if (bIndex === -1) return -1;
      return aIndex - bIndex;
    })
    .map((value) => ({ value, label: MOVEMENT_TYPE_LABELS[value] })),
];

const movementScopeOptions: Array<{ value: MovementScope; label: string }> = [
  { value: "all", label: "All Movements" },
  { value: "order_workflow", label: "Orders Workflow" },
  // Matches getMovementScopeLabel's badge text ("Internal") — filter option
  // and badge must agree.
  { value: "internal", label: "Internal" },
];

const prodEventTypeOptions: Array<{ value: ProductionEventType | "all"; label: string }> = [
  { value: "all", label: "All Events" },
  { value: "LABEL_PRINTED", label: "Label Printed" },
  { value: "PICKED", label: "Picked" },
  { value: "IN_PRETREAT", label: "In Pretreat" },
  { value: "CAUGHT_ACCEPT", label: "Caught Accept" },
  { value: "CAUGHT_REDO", label: "Caught Redo" },
  { value: "CATCH_REVERTED", label: "Catch Reverted" },
  { value: "BIN_ASSIGNED", label: "Bin Assigned" },
  { value: "BIN_RELEASED", label: "Bin Released" },
  { value: "BIN_MOVED_TO_OOS", label: "Bin Moved to OOS" },
  { value: "BIN_FORCE_RELEASED", label: "Bin Force Released" },
  { value: "PACKED", label: "Packed" },
  { value: "SHIPPED", label: "Shipped" },
  { value: "READY_FOR_PICKUP", label: "Ready for Pickup" },
  { value: "ORDER_ROUTED", label: "Order Routed" },
  { value: "ORDER_BLOCKED", label: "Order Blocked" },
  { value: "ORDER_RESUMED", label: "Order Resumed" },
  { value: "STATUS_OVERRIDE", label: "Status Override" },
  { value: "INVENTORY_ERROR", label: "Inventory Error" },
  { value: "PAP_DAMAGED", label: "PAP Damaged" },
  { value: "PICKING_DEFECTIVE", label: "Picking Defective" },
  { value: "MARKED_DEFECT", label: "Marked Defect" },
  { value: "PRINT_WRONG_ITEM", label: "Print Wrong Item" },
  { value: "PRINT_GARMENT_ISSUE", label: "Print Garment Issue" },
  { value: "CANCELLED_RETURNED", label: "Cancelled/Returned" },
  { value: "SHOPIFY_FULFILLMENT_ORDER_MOVED", label: "Shopify FO Moved" },
  { value: "FILM_CLAIMED_FROM_SHELF", label: "Film Claimed from Shelf" },
  { value: "FILM_DROP_OK", label: "Film Drop OK" },
  { value: "FILM_DROP_FAILED", label: "Film Drop Failed" },
  { value: "PORTLESS_MANIFEST_RECOVERED", label: "Portless Manifest Recovered" },
  { value: "TEST_BATCH_CREATED", label: "Test Batch Created" },
  { value: "TEST_DATA_DELETED", label: "Test Data Deleted" },
];

const formatStatusLabel = (status: unknown) =>
  typeof status === "string"
    ? status.split("_").map((w) => w.charAt(0) + w.slice(1).toLowerCase()).join(" ")
    : String(status);

const formatMetadata = (metadata: Record<string, unknown> | null) => {
  if (!metadata || Object.keys(metadata).length === 0) return null;
  const parts: string[] = [];
  if (metadata.reason) parts.push(`Reason: ${metadata.reason}`);
  if (metadata.fromStatus && metadata.toStatus)
    parts.push(`${formatStatusLabel(metadata.fromStatus)} → ${formatStatusLabel(metadata.toStatus)}`);
  if (metadata.binLabel) parts.push(`Bin: ${metadata.binLabel}`);
  if (metadata.trackingNumber) parts.push(`Tracking: ${metadata.trackingNumber}`);
  if (metadata.carrier) parts.push(`Carrier: ${metadata.carrier}`);
  if (metadata.sku) parts.push(`SKU: ${metadata.sku}`);
  if (parts.length > 0) return parts.join(" · ");
  return JSON.stringify(metadata);
};

const getShopifyStoreSlug = (domain?: string | null) => {
  if (!domain) return null;
  const cleaned = domain.trim().replace(/^https?:\/\//i, "").split("/")[0] ?? "";
  if (!cleaned) return null;
  return cleaned.replace(/\.myshopify\.com$/i, "");
};

const formatDateTime = (value?: string | null) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString("en-US", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const getMovementScopeLabel = (
  source: string | null | undefined,
  referenceType: string | null | undefined,
  movementType: MovementType,
) => {
  // BLU-811: committed/released rows are Internal-scoped by decision (the
  // server's scope filter puts them there) even though they now carry an
  // order reference_type — the badge must agree with the tab that returns
  // the row.
  if (movementType === "committed" || movementType === "released" || movementType === "backup_commit") {
    return "Internal";
  }
  const lowerSource = (source ?? "").toLowerCase();
  const lowerRef = (referenceType ?? "").toLowerCase();
  const isOrderWorkflow =
    lowerSource.startsWith("shopify_") || lowerRef === "order_id" || lowerRef === "shopify_order_id";

  return isOrderWorkflow ? "Orders Workflow" : "Internal";
};

type TabValue = "orders" | "stock_movements" | "shopify_push" | "production_events" | "bulk_imports" | "receive_history";
const VALID_TABS: TabValue[] = ["orders", "stock_movements", "shopify_push", "production_events", "bulk_imports", "receive_history"];

const LogsPage = () => {
  const { t } = useTranslation('inventory');
  const { selectedLocation, isLoading: locationsLoading } = useLocation();
  // `allLocations`, not `locations`: every use below RESOLVES A NAME on a
  // historical log row. A movement recorded at a location whose fulfilment was
  // later delegated must still read as that location, not as a raw uuid.
  const { allLocations } = useLocations();
  const { organization } = useOrganization();
  const trpcUtils = trpc.useUtils();
  const { toast } = useToast();
  const { format: formatCurrency } = useCurrencyFormatter();

  usePageTitle(t('logs.title'), 'Order sync health, stock movements, and production events.');

  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get("tab") as TabValue | null;
  const [tab, setTab] = useState<TabValue>(
    initialTab && VALID_TABS.includes(initialTab) ? initialTab : "orders"
  );
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<MatchFilter>("all");
  const [orderWindowDays, setOrderWindowDays] = useState<number>(ORDER_WINDOW_DEFAULT_DAYS);
  const [expandedOrders, setExpandedOrders] = useState<Set<string>>(new Set());
  const [movementPage, setMovementPage] = useState(1);
  const [movementSku, setMovementSku] = useState("");
  const [movementReference, setMovementReference] = useState("");
  // Exact-match mode: armed by the Order/Ref click-to-filter so PO-229 does
  // not also match PO-2291; disarmed the moment the operator types.
  const [movementReferenceExact, setMovementReferenceExact] = useState(false);
  const [movementType, setMovementType] = useState<MovementType | "all">("all");
  const [movementScope, setMovementScope] = useState<MovementScope>("all");
  const [movementDateFrom, setMovementDateFrom] = useState<Date | undefined>(undefined);
  const [movementDateTo, setMovementDateTo] = useState<Date | undefined>(undefined);
  const [isExporting, setIsExporting] = useState(false);
  const [pushPage, setPushPage] = useState(0);
  const [pushStatus, setPushStatus] = useState<PushStatusFilter | "all">("all");
  const [pushDateFrom, setPushDateFrom] = useState<Date | undefined>(undefined);
  const [pushDateTo, setPushDateTo] = useState<Date | undefined>(undefined);
  const [prodEventPage, setProdEventPage] = useState(1);
  const [prodEventType, setProdEventType] = useState<ProductionEventType | "all">("all");
  const [prodEventSearch, setProdEventSearch] = useState("");
  // 3PL per-client filter (Thread Lab stock log): shared across the Stock
  // Movements / Production Events / Adjustments tabs. Server-side — Logs is
  // paginated at 50/page, so client-side row filtering would silently
  // filter only the visible page.
  const [clientOrgFilter, setClientOrgFilter] = useState<string>(ALL_CLIENTS);

  useEffect(() => {
    setPage(1);
    setMovementPage(1);
    setProdEventPage(1);
    setExpandedOrders(new Set());
    setClientOrgFilter(ALL_CLIENTS);
  }, [selectedLocation?.id]);

  useEffect(() => {
    setPage(1);
    setExpandedOrders(new Set());
  }, [orderWindowDays]);

  useEffect(() => {
    setMovementPage(1);
  }, [movementSku, movementReference, movementType, movementScope, movementDateFrom, movementDateTo, clientOrgFilter]);

  useEffect(() => {
    setProdEventPage(1);
  }, [prodEventType, prodEventSearch, clientOrgFilter]);

  // Connected clients at the selected shared location — drives the filter's
  // visibility, options, and name resolution. React Query dedupes against
  // the inventory surfaces using the same hook.
  const { clients: connectedClients } = useConnectedClients({
    locationId: selectedLocation?.id,
    enabled: SHARED_LOCATIONS_ENABLED && !!organization?.id && !!selectedLocation?.id,
  });
  const showClientFilter =
    SHARED_LOCATIONS_ENABLED && !!selectedLocation?.id && connectedClients.length > 0;
  const clientOrgId =
    showClientFilter && clientOrgFilter !== ALL_CLIENTS ? clientOrgFilter : undefined;
  const clientOrgIdToName = useMemo(() => {
    const map = new Map<string, string>();
    if (organization?.id && organization?.name) {
      map.set(organization.id, `${organization.name} (You)`);
    }
    for (const c of connectedClients) {
      if (!map.has(c.id)) map.set(c.id, c.name);
    }
    return map;
  }, [organization?.id, organization?.name, connectedClients]);
  const clientFilterOptions = useMemo(
    () =>
      [organization?.id, ...connectedClients.map((c) => c.id)].filter(
        (id): id is string => Boolean(id),
      ),
    [organization?.id, connectedClients],
  );
  /** Display name for a row's owner org: backend-resolved name → local map → hex tag. */
  const ownerOrgLabel = (id: string | null | undefined, name: string | null | undefined) => {
    if (!id) return "-";
    return name ?? clientOrgIdToName.get(id) ?? t('crossOrg.partnerFallbackHex', { hex: hexSlice(id) });
  };

  /**
   * BLU-711: Client attribution for a stock-movement row. Substitution rows
   * (source='shared_sku_substitution') are stamped with the OWNER org on the
   * row (whose shared pool was decremented) and the CLIENT org on the actor,
   * so the Client column keys off the actor for those rows; every other row
   * keeps the row-org attribution. Used by BOTH the table cell and the CSV
   * export so the two never drift.
   */
  const movementClientLabel = (row: {
    source: string | null;
    ownerOrganizationId: string;
    ownerOrganizationName: string | null;
    actorOrganizationId: string | null;
    actorOrganizationName: string | null;
  }) => {
    if (row.source === "shared_sku_substitution" && row.actorOrganizationId) {
      return ownerOrgLabel(row.actorOrganizationId, row.actorOrganizationName);
    }
    // BLU-1428: the 3PL's back-up line rows (owner-only) belong to the client
    // whose order moved there: the actor org.
    if (row.source === "backup_commit" && row.actorOrganizationId) {
      return ownerOrgLabel(row.actorOrganizationId, row.actorOrganizationName);
    }
    return ownerOrgLabel(row.ownerOrganizationId, row.ownerOrganizationName);
  };

  /**
   * BLU-1428: the Notes text for a stock-movement row (back-up commit rows
   * are built from `backupCommit`; BLU-1527 own substitute rows say so, by
   * the server's `kind`). Shared by the table and the CSV export.
   */
  const movementNotesLabel = (row: MovementNotesRow): string | null =>
    movementNotesText(t, row, {
      orgLabel: (id, name) => ownerOrgLabel(id, name),
      formatPrice: (amount, currency) => formatSupplyPrice(amount, currency),
    });

  /**
   * BLU-1527: the Type of a stock-movement row. A back-up commit row is
   * "Substitute garment" (the org's own) or "3PL back-up", by the server's
   * `kind`; every other type keeps its badge. Shared by the table and the
   * CSV export.
   */
  const movementRowTypeStyle = (row: {
    movementType: MovementType;
    backupCommit?: MovementNotesRow["backupCommit"];
  }) => {
    const style = movementTypeStyle(row.movementType);
    return row.movementType === "backup_commit"
      ? { ...style, label: backupCommitTypeLabel(t, row.backupCommit?.kind) }
      : style;
  };


  // Shared by the table query and the CSV export below — they must always take
  // the same window, or the file describes a different set than the screen.
  const orderSyncFilters = {
    locationId: selectedLocation?.id ?? undefined,
    windowDays: orderWindowDays,
  };

  // BLU-3xx Phase 47.01.2-B-fix3.6 (#5): gate the location-scoped queries on
  // `!locationsLoading` so the initial fetch waits for LocationContext to
  // settle. Pre-fix3.6 the queries fired with `locationId: undefined` (=
  // "All locations" mode) before LocationContext picked a default, then
  // refetched scoped — surfacing the wider dataset for a few seconds.
  const orderSyncQuery = trpc.logs.getOrderSyncHealth.useQuery(
    {
      ...orderSyncFilters,
      page,
      limit: PAGE_SIZE,
    },
    {
      enabled: !locationsLoading,
      staleTime: 30_000,
    }
  );

  // BLU-589: date range + derived-status filters threaded into the push query.
  // Undefined keys are dropped by tRPC/superjson, so "All" sends no filter.
  const pushFilters = useMemo(
    () => ({
      status: pushStatus === "all" ? undefined : pushStatus,
      fromDate: pushDateFrom ? toLocalDateString(pushDateFrom) : undefined,
      toDate: pushDateTo ? toLocalDateString(pushDateTo) : undefined,
    }),
    [pushStatus, pushDateFrom, pushDateTo],
  );
  const pushHasActiveFilters =
    pushStatus !== "all" || Boolean(pushDateFrom) || Boolean(pushDateTo);
  const clearPushFilters = () => {
    setPushStatus("all");
    setPushDateFrom(undefined);
    setPushDateTo(undefined);
    setPushPage(0);
  };

  const pushHistoryQuery = usePushHistory(pushPage, pushFilters);

  const stockMovementQuery = trpc.logs.getStockMovements.useQuery(
    {
      locationId: selectedLocation?.id ?? undefined,
      sku: movementSku.trim() || undefined,
      reference: movementReference.trim() || undefined,
      referenceExact: movementReferenceExact || undefined,
      clientOrgId,
      movementType: movementType === "all" ? undefined : movementType,
      scope: movementScope === "all" ? undefined : movementScope,
      dateFrom: movementDateFrom ? toLocalDateString(movementDateFrom) : undefined,
      dateTo: movementDateTo ? toLocalDateString(movementDateTo) : undefined,
      page: movementPage,
      limit: PAGE_SIZE,
    },
    {
      enabled: !locationsLoading && tab === 'stock_movements',
      staleTime: 30_000,
    }
  );

  const prodEventsQuery = trpc.logs.getProductionEvents.useQuery(
    {
      locationId: selectedLocation?.id ?? undefined,
      clientOrgId,
      eventType: prodEventType === "all" ? undefined : prodEventType,
      search: prodEventSearch.trim() || undefined,
      page: prodEventPage,
      limit: PAGE_SIZE,
    },
    {
      enabled: !locationsLoading && tab === 'production_events',
      staleTime: 30_000,
    }
  );

  const clientFilterNode =
    showClientFilter && organization ? (
      <ClientFilterDropdown
        value={clientOrgFilter}
        onChange={setClientOrgFilter}
        ownOrgId={organization.id}
        orgIdToName={clientOrgIdToName}
        distinctOrgsInData={clientFilterOptions}
      />
    ) : null;

  const movementColSpan = showClientFilter ? 13 : 12;
  const prodEventColSpan = showClientFilter ? 7 : 6;

  const locationNameById = useMemo(() => {
    return new Map(allLocations.map((location) => [location.id, location.name]));
  }, [allLocations]);

  const shopifyStoreSlug = useMemo(
    () => getShopifyStoreSlug(organization?.shopify_domain),
    [organization?.shopify_domain]
  );

  const buildShopifyOrderUrl = (shopifyOrderId: number | null) => {
    if (!shopifyStoreSlug || !shopifyOrderId) return null;
    return `https://admin.shopify.com/store/${shopifyStoreSlug}/orders/${shopifyOrderId}`;
  };

  const rows = useMemo(() => orderSyncQuery.data?.data ?? [], [orderSyncQuery.data?.data]);
  const pagination = orderSyncQuery.data?.pagination;
  const movementRows = useMemo(
    () => stockMovementQuery.data?.data ?? [],
    [stockMovementQuery.data?.data]
  );
  const movementPagination = stockMovementQuery.data?.pagination;

  const filteredRows = useMemo(() => {
    const needle = search.trim().toLowerCase();

    return rows.filter((row) => {
      const statusMatches =
        statusFilter === "all"
          ? true
          : statusFilter === "issues"
            ? row.matchStatus !== "all_matched"
            : row.matchStatus === statusFilter;

      if (!statusMatches) return false;
      if (!needle) return true;

      const searchable = [
        row.orderNumber ?? "",
        row.orderName ?? "",
        row.shopifyOrderId ? String(row.shopifyOrderId) : "",
        row.status ?? "",
        ...row.fullSkus,
        ...row.cleanedSkus,
        ...row.unmatchedSkus,
        ...row.items.map((item) => item.sku),
      ]
        .join(" ")
        .toLowerCase();

      return searchable.includes(needle);
    });
  }, [rows, search, statusFilter]);

  const summary = useMemo(() => {
    let issueOrders = 0;
    let unmatchedSkus = 0;

    for (const row of rows) {
      if (row.matchStatus !== "all_matched") issueOrders += 1;
      unmatchedSkus += row.unmatchedLineItems;
    }

    return {
      totalOrders: rows.length,
      issueOrders,
      unmatchedSkus,
    };
  }, [rows]);

  const handleRefresh = async () => {
    await Promise.all([orderSyncQuery.refetch(), stockMovementQuery.refetch(), pushHistoryQuery.refetch(), prodEventsQuery.refetch()]);
  };

  const toggleExpanded = (orderId: string) => {
    setExpandedOrders((prev) => {
      const next = new Set(prev);
      if (next.has(orderId)) {
        next.delete(orderId);
      } else {
        next.add(orderId);
      }
      return next;
    });
  };

  if (locationsLoading) {
    return (
      <RoleGuard requirePurchaseOrderAccess>
        <div className="flex flex-col">
          <PageLoader pool="logs" />
        </div>
      </RoleGuard>
    );
  }

  return (
    <RoleGuard requirePurchaseOrderAccess>
      <div className="flex flex-col">
        <main className="flex-1 w-full py-6 px-4 sm:px-6 lg:px-8 space-y-6">
          <Tabs value={tab} onValueChange={(value) => setTab(value as TabValue)}>
            <PageActionBar
              ariaLabel="Logs actions"
              left={
                <TabsList className="flex flex-wrap h-auto">
              <TabsTrigger value="orders" className="gap-1.5">
                <Boxes className="h-3.5 w-3.5" aria-hidden="true" />
                <span>Orders</span>
                {tab === 'orders' && orderSyncQuery.data?.pagination?.total != null && (
                  <span className="ml-1 text-muted-foreground tabular-nums">
                    · {orderSyncQuery.data.pagination.total.toLocaleString()}
                  </span>
                )}
              </TabsTrigger>
              <TabsTrigger value="stock_movements" className="gap-1.5">
                <ArrowRightLeft className="h-3.5 w-3.5" aria-hidden="true" />
                <span>Stock Movements</span>
                {tab === 'stock_movements' && movementPagination?.total != null && (
                  <span className="ml-1 text-muted-foreground tabular-nums">
                    · {movementPagination.total.toLocaleString()}
                  </span>
                )}
              </TabsTrigger>
              <TabsTrigger value="shopify_push" className="gap-1.5">
                <Upload className="h-3.5 w-3.5" aria-hidden="true" />
                <span>Shopify Push</span>
                {tab === 'shopify_push' && pushHistoryQuery.data?.total != null && (
                  <span className="ml-1 text-muted-foreground tabular-nums">
                    · {pushHistoryQuery.data.total.toLocaleString()}
                  </span>
                )}
              </TabsTrigger>
              <TabsTrigger value="production_events" className="gap-1.5">
                <Workflow className="h-3.5 w-3.5" aria-hidden="true" />
                <span>Production Events</span>
                {tab === 'production_events' && prodEventsQuery.data?.pagination?.total != null && (
                  <span className="ml-1 text-muted-foreground tabular-nums">
                    · {prodEventsQuery.data.pagination.total.toLocaleString()}
                  </span>
                )}
              </TabsTrigger>
              <TabsTrigger value="bulk_imports" className="gap-1.5">
                <FileSpreadsheet className="h-3.5 w-3.5" aria-hidden="true" />
                {/* Renamed from "Bulk Imports": batches now cover the
                    multi-select bulk add too — they are adjustment documents,
                    not just CSV uploads. Tab id stays `bulk_imports` so
                    ?tab= deep links keep working. */}
                <span>Adjustments</span>
              </TabsTrigger>
              <TabsTrigger value="receive_history" className="gap-1.5">
                <PackageCheck className="h-3.5 w-3.5" aria-hidden="true" />
                <span>Receive History</span>
              </TabsTrigger>
                </TabsList>
              }
              right={
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 px-3 text-xs gap-1.5"
                  onClick={() => void handleRefresh()}
                  disabled={orderSyncQuery.isFetching || stockMovementQuery.isFetching || pushHistoryQuery.isFetching || prodEventsQuery.isFetching}
                >
                  <RefreshCw
                    className={cn(
                      "h-3.5 w-3.5",
                      (orderSyncQuery.isFetching || stockMovementQuery.isFetching || pushHistoryQuery.isFetching || prodEventsQuery.isFetching) && "animate-spin"
                    )}
                  />
                  Refresh
                </Button>
              }
            />

            <TabsContent value="orders" className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <MetricCard
                  variant="production"
                  title="Orders on Page"
                  value={summary.totalOrders.toLocaleString()}
                  icon={ClipboardList}
                />
                <MetricCard
                  variant="warning"
                  title="Orders with Issues"
                  value={summary.issueOrders.toLocaleString()}
                  icon={AlertCircle}
                />
                <MetricCard
                  variant="danger"
                  title="Unmatched Line Items"
                  value={summary.unmatchedSkus.toLocaleString()}
                  icon={XCircle}
                />
              </div>

              <div className="flex flex-wrap items-center gap-3">
                <div className="relative w-full max-w-sm">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search order, SKU, status..."
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    className="pl-10"
                  />
                </div>

                <Select
                  value={statusFilter}
                  onValueChange={(value) => setStatusFilter(value as MatchFilter)}
                >
                  <SelectTrigger className="w-[220px]">
                    <SelectValue placeholder="Filter status" />
                  </SelectTrigger>
                  <SelectContent>
                    {matchFilterOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select
                  value={String(orderWindowDays)}
                  onValueChange={(value) => setOrderWindowDays(Number(value))}
                >
                  <SelectTrigger className="w-[180px]" aria-label={t('logs.orders.window.label')}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ORDER_WINDOW_OPTIONS.map((days) => (
                      <SelectItem key={days} value={String(days)}>
                        {t('logs.orders.window.option', { days })}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Button
                  variant="outline"
                  onClick={() => {
                    setSearch("");
                    setStatusFilter("all");
                    setOrderWindowDays(ORDER_WINDOW_DEFAULT_DAYS);
                  }}
                >
                  Clear
                </Button>

                <div className="ml-auto">
                  <Button
                    variant="outline"
                    disabled={isExporting || (pagination?.total ?? 0) === 0}
                    onClick={async () => {
                      const totalCount = pagination?.total ?? 0;
                      if (totalCount > EXPORT_ROW_CAP) {
                        toast({
                          title: `Too many rows to export (${totalCount.toLocaleString()})`,
                          description: `Narrow filters to fewer than ${EXPORT_ROW_CAP.toLocaleString()} rows.`,
                          variant: "destructive",
                        });
                        return;
                      }
                      setIsExporting(true);
                      try {
                        const exportData = await trpcUtils.logs.exportOrderSyncHealth.fetch({
                          ...orderSyncFilters,
                          page: 1,
                          limit: EXPORT_ROW_CAP,
                        });
                        const locName = selectedLocation?.name ?? '';
                        const csvRows: unknown[][] = [
                          ['Processed', 'Order Name', 'Shopify Order ID', 'Status', 'Match Status', 'Matched', 'Unmatched', 'Total Items', 'Matched SKUs', 'Unmatched SKUs', 'Location'],
                          ...exportData.data.map((row) => [
                            row.processedAt ?? row.createdAt ?? '',
                            row.orderName ?? row.orderNumber ?? '',
                            row.shopifyOrderId ?? '',
                            row.status ?? '',
                            row.matchStatus,
                            row.matchedLineItems,
                            row.unmatchedLineItems,
                            row.totalLineItems,
                            row.cleanedSkus.join('|'),
                            row.unmatchedSkus.join('|'),
                            locName,
                          ]),
                        ];
                        downloadCsv(csvRows, `orders-${formatDate(new Date(), 'yyyy-MM-dd')}.csv`);
                      } catch (err) {
                        toast({
                          title: "Export failed",
                          description: err instanceof Error ? err.message : "Failed to export orders",
                          variant: "destructive",
                        });
                      } finally {
                        setIsExporting(false);
                      }
                    }}
                    className="gap-1.5"
                  >
                    <Download className="h-4 w-4" />
                    {isExporting ? "Exporting..." : "Export CSV"}
                  </Button>
                </div>
              </div>

              <p className="text-sm text-muted-foreground">
                {t('logs.orders.window.note', {
                  days: orderSyncQuery.data?.window.days ?? orderWindowDays,
                })}
              </p>

              <div className="bg-card rounded-lg shadow border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[56px]">Details</TableHead>
                      <TableHead className="w-[190px]">Processed</TableHead>
                      <TableHead>Order</TableHead>
                      <TableHead className="w-[180px]">Match Status</TableHead>
                      <TableHead className="w-[120px] text-right">Matched</TableHead>
                      <TableHead className="w-[130px] text-right">Unmatched</TableHead>
                      <TableHead className="w-[130px] text-right">Total Items</TableHead>
                      <TableHead className="w-[180px]">Location</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {orderSyncQuery.isLoading ? (
                      <TableRow>
                        <TableCell colSpan={8} className="py-10 text-center text-muted-foreground">
                          Loading logs...
                        </TableCell>
                      </TableRow>
                    ) : orderSyncQuery.error ? (
                      <TableRow>
                        <TableCell colSpan={8} className="p-0">
                          <PageErrorState
                            title="Couldn't load order logs"
                            description={orderSyncQuery.error.message}
                            onRetry={() => orderSyncQuery.refetch()}
                            retrying={orderSyncQuery.isFetching}
                          />
                        </TableCell>
                      </TableRow>
                    ) : filteredRows.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} className="p-0">
                          <EmptyState
                            icon={ClipboardList}
                            title="No logs found"
                            description="No order logs match the current filters."
                          />
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredRows.map((row) => {
                        const expanded = expandedOrders.has(row.orderId);
                        const orderLabel =
                          row.orderNumber ||
                          row.orderName ||
                          (row.shopifyOrderId ? `#${row.shopifyOrderId}` : row.orderId);
                        const shopifyOrderUrl = buildShopifyOrderUrl(row.shopifyOrderId);
                        const locationLabel = row.locationId
                          ? locationNameById.get(row.locationId) ?? row.locationId
                          : "-";

                        return (
                          <Fragment key={row.orderId}>
                            <TableRow
                              className="cursor-pointer hover:bg-muted/40"
                              onClick={() => toggleExpanded(row.orderId)}
                            >
                              <TableCell>
                                {expanded ? (
                                  <ChevronDown className="h-4 w-4 text-muted-foreground" />
                                ) : (
                                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                                )}
                              </TableCell>
                              <TableCell className="text-muted-foreground whitespace-nowrap">
                                {formatDateTime(row.processedAt ?? row.createdAt)}
                              </TableCell>
                              <TableCell>
                                <div className="flex flex-col gap-1">
                                  <span className="font-medium">{orderLabel}</span>
                                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                    <span>Shopify ID: {row.shopifyOrderId ?? "-"}</span>
                                    {shopifyOrderUrl ? (
                                      <a
                                        href={shopifyOrderUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center gap-1 text-primary hover:underline"
                                        onClick={(event) => event.stopPropagation()}
                                      >
                                        Open
                                        <ExternalLink className="h-3 w-3" />
                                      </a>
                                    ) : null}
                                  </div>
                                </div>
                              </TableCell>
                              <TableCell>
                                <StatusBadge status={matchStatusStyle(row.matchStatus)} />
                              </TableCell>
                              <TableCell className="text-right tabular-nums">{row.matchedLineItems}</TableCell>
                              <TableCell className="text-right tabular-nums">
                                <span
                                  className={row.unmatchedLineItems > 0 ? "text-red-700 font-medium" : "text-muted-foreground"}
                                >
                                  {row.unmatchedLineItems}
                                </span>
                              </TableCell>
                              <TableCell className="text-right tabular-nums">{row.totalLineItems}</TableCell>
                              <TableCell className="text-sm text-muted-foreground">{locationLabel}</TableCell>
                            </TableRow>

                            {expanded ? (
                              <TableRow className="bg-muted/20">
                                <TableCell colSpan={8}>
                                  <div className="space-y-4 py-2">
                                    <div className="flex flex-wrap gap-2">
                                      <Badge variant="outline">Status: {row.status ?? "unknown"}</Badge>
                                      <Badge variant="outline">Full SKUs: {row.fullSkus.length}</Badge>
                                      <Badge variant="outline">Cleaned SKUs: {row.cleanedSkus.length}</Badge>
                                      <Badge variant="outline" className="border-red-200 text-red-700">
                                        Unmatched SKUs: {row.unmatchedSkus.length}
                                      </Badge>
                                    </div>

                                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
                                      <div className="rounded-md border bg-background p-3">
                                        <p className="text-xs font-medium text-muted-foreground mb-2">Full SKUs</p>
                                        <p className="text-sm break-words">
                                          {row.fullSkus.length ? row.fullSkus.join(", ") : "-"}
                                        </p>
                                      </div>
                                      <div className="rounded-md border bg-background p-3">
                                        <p className="text-xs font-medium text-muted-foreground mb-2">Cleaned SKUs</p>
                                        <p className="text-sm break-words">
                                          {row.cleanedSkus.length ? row.cleanedSkus.join(", ") : "-"}
                                        </p>
                                      </div>
                                      <div className="rounded-md border bg-background p-3">
                                        <p className="text-xs font-medium text-muted-foreground mb-2">Unmatched SKUs</p>
                                        <p className="text-sm break-words text-red-700">
                                          {row.unmatchedSkus.length ? row.unmatchedSkus.join(", ") : "-"}
                                        </p>
                                      </div>
                                    </div>

                                    <div className="rounded-md border bg-background overflow-hidden">
                                      <Table>
                                        <TableHeader>
                                          <TableRow>
                                            <TableHead className="w-[240px]">SKU</TableHead>
                                            <TableHead>Product</TableHead>
                                            <TableHead className="w-[100px] text-right">Qty</TableHead>
                                            <TableHead className="w-[160px]">Link Status</TableHead>
                                          </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                          {row.items.length === 0 ? (
                                            <TableRow>
                                              <TableCell colSpan={4} className="text-center py-6 text-muted-foreground">
                                                No order items found.
                                              </TableCell>
                                            </TableRow>
                                          ) : (
                                            row.items.map((item) => (
                                              <TableRow key={item.id}>
                                                <TableCell>
                                                  <Link
                                                    to={`/inventory?search=${encodeURIComponent(item.sku)}`}
                                                    className="font-mono text-sm text-primary hover:underline"
                                                  >
                                                    {item.sku}
                                                  </Link>
                                                </TableCell>
                                                <TableCell>
                                                  <div className="truncate max-w-[520px]" title={item.productTitle}>
                                                    {item.productTitle}
                                                    {item.variantTitle && item.variantTitle !== "Default Title" ? (
                                                      <span className="text-muted-foreground"> · {item.variantTitle}</span>
                                                    ) : null}
                                                  </div>
                                                </TableCell>
                                                <TableCell className="text-right tabular-nums">{item.quantity}</TableCell>
                                                <TableCell>
                                                  <Badge
                                                    className={
                                                      item.linked
                                                        ? "bg-emerald-100 text-emerald-800 hover:bg-emerald-100"
                                                        : "bg-red-100 text-red-800 hover:bg-red-100"
                                                    }
                                                  >
                                                    {item.linked ? "Matched" : "Unmatched"}
                                                  </Badge>
                                                </TableCell>
                                              </TableRow>
                                            ))
                                          )}
                                        </TableBody>
                                      </Table>
                                    </div>
                                  </div>
                                </TableCell>
                              </TableRow>
                            ) : null}
                          </Fragment>
                        );
                      })
                    )}
                  </TableBody>
                </Table>

                {pagination && pagination.totalPages > 1 ? (
                  <div className="flex items-center justify-between px-4 py-3 border-t">
                    <p className="text-sm text-muted-foreground">
                      Showing {(pagination.page - 1) * pagination.limit + 1} to{" "}
                      {Math.min(pagination.page * pagination.limit, pagination.total)} of{" "}
                      {pagination.total} orders
                    </p>
                    <div className="flex space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={pagination.page <= 1}
                        onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                      >
                        Previous
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={pagination.page >= pagination.totalPages}
                        onClick={() => setPage((prev) => Math.min(pagination.totalPages, prev + 1))}
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                ) : null}
              </div>
            </TabsContent>

            <TabsContent value="stock_movements" className="space-y-4">
              <div className="flex flex-wrap items-center gap-3">
                <Input
                  placeholder="Filter by SKU"
                  value={movementSku}
                  onChange={(event) => setMovementSku(event.target.value)}
                  className="w-56"
                />
                <Input
                  placeholder="Filter by order/reference"
                  value={movementReference}
                  onChange={(event) => {
                    setMovementReference(event.target.value);
                    // Typing always returns to substring mode; exact mode is
                    // only armed by the Order/Ref click-to-filter.
                    setMovementReferenceExact(false);
                  }}
                  className="w-64"
                />
                <Select
                  value={movementScope}
                  onValueChange={(value) => setMovementScope(value as MovementScope)}
                >
                  <SelectTrigger className="w-[220px]">
                    <SelectValue placeholder="Scope" />
                  </SelectTrigger>
                  <SelectContent>
                    {movementScopeOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select
                  value={movementType}
                  onValueChange={(value) => setMovementType(value as MovementType | "all")}
                >
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Type" />
                  </SelectTrigger>
                  <SelectContent>
                    {movementTypeOptions
                      // Committed/Released are Internal-scoped rows (BLU-811)
                      // — they can never match the Orders Workflow scope, so
                      // offering them there reads as a bug.
                      .filter(
                        (option) =>
                          movementScope !== "order_workflow" ||
                          (option.value !== "committed" &&
                            option.value !== "released" &&
                            option.value !== "backup_commit"),
                      )
                      .map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {/* One server-side type for both kinds of back-up commit row. */}
                          {option.value === "backup_commit" ? backupCommitFilterLabel(t) : option.label}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
                {clientFilterNode}
                <Button
                  variant="outline"
                  onClick={() => {
                    setMovementSku("");
                    setMovementReference("");
                    setMovementReferenceExact(false);
                    setMovementScope("all");
                    setMovementType("all");
                    setMovementDateFrom(undefined);
                    setMovementDateTo(undefined);
                    setClientOrgFilter(ALL_CLIENTS);
                  }}
                >
                  Clear
                </Button>
              </div>

              {/* Date range + Export */}
              <div className="flex flex-wrap items-center gap-3">
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className={cn("w-[160px] justify-start text-left font-normal", !movementDateFrom && "text-muted-foreground")}>
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {movementDateFrom ? formatDate(movementDateFrom, "dd MMM yyyy") : "From date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={movementDateFrom}
                      onSelect={(date) => {
                        setMovementDateFrom(date);
                        if (date && movementDateTo && date > movementDateTo) {
                          setMovementDateTo(undefined);
                        }
                      }}
                      disabled={(date) => date > new Date()}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
                <span className="text-muted-foreground text-sm">to</span>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className={cn("w-[160px] justify-start text-left font-normal", !movementDateTo && "text-muted-foreground")}>
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {movementDateTo ? formatDate(movementDateTo, "dd MMM yyyy") : "To date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={movementDateTo}
                      onSelect={setMovementDateTo}
                      disabled={(date) => date > new Date() || (movementDateFrom ? date < movementDateFrom : false)}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>

                <div className="ml-auto">
                  <Button
                    variant="outline"
                    disabled={isExporting || (movementPagination?.total ?? 0) === 0}
                    onClick={async () => {
                      // Check total count first — warn if very large (before showing loading state)
                      const totalCount = movementPagination?.total ?? 0;
                      if (totalCount > 10000) {
                        toast({
                          title: `Too many rows to export (${totalCount.toLocaleString()})`,
                          description: "Use date range and filters to narrow below 10,000 rows.",
                          variant: "destructive",
                        });
                        return;
                      }
                      setIsExporting(true);
                      try {

                        // Export mirrors the on-screen filters — including the
                        // client filter, applied server-side, never post-hoc.
                        const exportData = await trpcUtils.logs.exportStockMovements.fetch({
                          locationId: selectedLocation?.id ?? undefined,
                          sku: movementSku.trim() || undefined,
                          reference: movementReference.trim() || undefined,
                          referenceExact: movementReferenceExact || undefined,
                          clientOrgId,
                          movementType: movementType === "all" ? undefined : movementType,
                          scope: movementScope === "all" ? undefined : movementScope,
                          dateFrom: movementDateFrom ? toLocalDateString(movementDateFrom) : undefined,
                          dateTo: movementDateTo ? toLocalDateString(movementDateTo) : undefined,
                          page: 1,
                          limit: 10000,
                        });

                        const allRows = exportData.data;

                        const csvRows: unknown[][] = [
                          ['Date', 'SKU', 'Product', 'Scope', 'Type', 'Qty Change', 'Stock Before', 'Stock After', 'Unit Cost', 'Value Change', 'Order/Reference', 'Client', 'Actor', 'Notes'],
                          ...allRows.map((row) => [
                            row.createdAt,
                            row.sku,
                            row.productName ?? '',
                            getMovementScopeLabel(row.source, row.referenceType, row.movementType),
                            movementRowTypeStyle(row).label,
                            row.quantity,
                            // BLU-711: null on cross-org substitution rows —
                            // the owner-pool figures are redacted server-side.
                            row.stockBefore ?? '',
                            row.stockAfter ?? '',
                            row.unitCostUsd ?? '',
                            row.costChangeUsd ?? '',
                            row.reference ?? row.referenceType ?? '',
                            movementClientLabel(row),
                            row.userName ?? '',
                            movementNotesLabel(row) ?? '',
                          ]),
                        ];

                        downloadCsv(csvRows, `stock-movements-${formatDate(new Date(), 'yyyy-MM-dd')}.csv`);
                      } catch (err) {
                        toast({
                          title: "Export failed",
                          description: err instanceof Error ? err.message : "Failed to export stock movements",
                          variant: "destructive",
                        });
                      } finally {
                        setIsExporting(false);
                      }
                    }}
                    className="gap-1.5"
                  >
                    <Download className="h-4 w-4" />
                    {isExporting ? "Exporting..." : "Export CSV"}
                  </Button>
                </div>
              </div>

              <div className="bg-card rounded-lg shadow border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[180px]">Date</TableHead>
                      <TableHead className="w-[220px]">SKU</TableHead>
                      <TableHead>Product</TableHead>
                      <TableHead className="w-[170px]">Scope</TableHead>
                      <TableHead className="w-[120px]">Type</TableHead>
                      <TableHead className="w-[120px] text-right">Qty</TableHead>
                      <TableHead className="w-[110px] text-right hidden sm:table-cell">Balance</TableHead>
                      <TableHead className="w-[110px] text-right hidden sm:table-cell">Unit Cost</TableHead>
                      <TableHead className="w-[110px] text-right hidden sm:table-cell">Value</TableHead>
                      <TableHead className="w-[170px]">Order/Ref</TableHead>
                      {showClientFilter && <TableHead className="w-[160px]">Client</TableHead>}
                      <TableHead className="w-[170px]">Actor</TableHead>
                      <TableHead>Notes</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {stockMovementQuery.isLoading ? (
                      <TableRow>
                        <TableCell colSpan={movementColSpan} className="py-10 text-center text-muted-foreground">
                          Loading stock movements...
                        </TableCell>
                      </TableRow>
                    ) : stockMovementQuery.error ? (
                      <TableRow>
                        <TableCell colSpan={movementColSpan} className="p-0">
                          <PageErrorState
                            title="Couldn't load stock movements"
                            description={stockMovementQuery.error.message}
                            onRetry={() => stockMovementQuery.refetch()}
                            retrying={stockMovementQuery.isFetching}
                          />
                        </TableCell>
                      </TableRow>
                    ) : movementRows.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={movementColSpan} className="p-0">
                          <EmptyState
                            icon={ArrowRightLeft}
                            title="No stock movements found"
                            description="No stock movements match the current filters."
                          />
                        </TableCell>
                      </TableRow>
                    ) : (
                      movementRows.map((row) => {
                        const scopeLabel = getMovementScopeLabel(row.source, row.referenceType, row.movementType);
                        const shopifyOrderUrl = buildShopifyOrderUrl(row.shopifyOrderId ?? null);
                        // BLU-811: committed/released rows are committed-space,
                        // not a physical stock movement — the Qty/Balance
                        // columns render neutral for them (see below).
                        const isCommittedSpace =
                          row.movementType === "committed" ||
                          row.movementType === "released" ||
                          row.movementType === "backup_commit";
                        // BLU-3xx Phase 47.01.2-B-fix3.6 (#4): rely on the
                        // backend's friendly reference (now with branches for
                        // receipt_reversal, committed_quantity, etc.) and
                        // fall back to em-dash. Pre-fix3.6 we surfaced the
                        // raw `referenceType` enum name (e.g. "committed_quantity")
                        // when reference was null.
                        const referenceLabel =
                          row.reference ??
                          (row.shopifyOrderId ? `#${row.shopifyOrderId}` : "-");

                        return (
                          <TableRow key={row.id}>
                            <TableCell className="whitespace-nowrap text-muted-foreground">
                              {formatDateTime(row.createdAt)}
                            </TableCell>
                            <TableCell>
                              <Link
                                to={row.productId ? `/products/${row.productId}` : `/inventory?search=${encodeURIComponent(row.sku)}`}
                                className="font-mono text-sm text-primary hover:underline"
                              >
                                {row.sku}
                              </Link>
                            </TableCell>
                            <TableCell className="text-muted-foreground">{row.productName ?? "-"}</TableCell>
                            <TableCell>
                              <Badge variant="outline">{scopeLabel}</Badge>
                            </TableCell>
                            <TableCell>
                              <StatusBadge status={movementRowTypeStyle(row)} />
                            </TableCell>
                            <TableCell className="text-right tabular-nums">
                              <span
                                className={
                                  isCommittedSpace
                                    ? "text-muted-foreground"
                                    : row.quantity > 0
                                      ? "text-emerald-700"
                                      : row.quantity < 0
                                        ? "text-red-700"
                                        : "text-muted-foreground"
                                }
                              >
                                {row.quantity > 0 ? "+" : ""}
                                {row.quantity}
                              </span>
                            </TableCell>
                            <TableCell className="text-right tabular-nums hidden sm:table-cell">
                              {isCommittedSpace ? (
                                // BLU-811: stock_before/after on committed/
                                // released rows are committed-space
                                // (committed_quantity), not physical stock —
                                // rendering them here would lie in the
                                // Balance column.
                                <span className="text-muted-foreground">-</span>
                              ) : row.stockAfter != null ? (
                                <span
                                  className={
                                    row.stockAfter < 0
                                      ? "text-amber-600 dark:text-amber-500"
                                      : row.stockAfter === 0
                                        ? "text-muted-foreground"
                                        : undefined
                                  }
                                >
                                  {row.stockAfter.toLocaleString()}
                                </span>
                              ) : (
                                // BLU-711: owner-pool figure redacted server-side
                                // on cross-org substitution rows — same muted
                                // dash the cost columns use.
                                <span className="text-muted-foreground">-</span>
                              )}
                            </TableCell>
                            <TableCell className="text-right tabular-nums hidden sm:table-cell">
                              {row.unitCostUsd != null ? (
                                formatCurrency(row.unitCostUsd)
                              ) : (
                                <span className="text-muted-foreground">-</span>
                              )}
                            </TableCell>
                            <TableCell className="text-right tabular-nums hidden sm:table-cell">
                              {row.costChangeUsd != null ? (
                                <span
                                  className={
                                    row.costChangeUsd > 0
                                      ? "text-emerald-700"
                                      : row.costChangeUsd < 0
                                        ? "text-red-700"
                                        : "text-muted-foreground"
                                  }
                                >
                                  {row.costChangeUsd > 0 ? "+" : ""}
                                  {formatCurrency(row.costChangeUsd)}
                                </span>
                              ) : (
                                <span className="text-muted-foreground">-</span>
                              )}
                            </TableCell>
                            <TableCell className="whitespace-nowrap">
                              {shopifyOrderUrl ? (
                                <a
                                  href={shopifyOrderUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center text-primary hover:underline"
                                >
                                  {referenceLabel}
                                  <ExternalLink className="h-3 w-3 ml-1" />
                                </a>
                              ) : row.reference &&
                                CLICK_FILTERABLE_REFERENCE_TYPES.has(row.referenceType ?? "") ? (
                                // Click-to-filter: one adjustment = one list.
                                // Exact match so PO-229 doesn't pull in PO-2291.
                                <button
                                  type="button"
                                  className="text-primary hover:underline"
                                  title="Filter by this reference"
                                  onClick={() => {
                                    setMovementReference(row.reference ?? "");
                                    setMovementReferenceExact(true);
                                  }}
                                >
                                  {referenceLabel}
                                </button>
                              ) : (
                                <span className="text-muted-foreground">{referenceLabel}</span>
                              )}
                            </TableCell>
                            {showClientFilter && (
                              <TableCell className="text-muted-foreground text-sm">
                                {movementClientLabel(row)}
                              </TableCell>
                            )}
                            <TableCell className="text-muted-foreground">{row.userName ?? "-"}</TableCell>
                            <TableCell className="text-muted-foreground">{movementNotesLabel(row) ?? "-"}</TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>

                {movementPagination && movementPagination.totalPages > 1 ? (
                  <div className="flex items-center justify-between px-4 py-3 border-t">
                    <p className="text-sm text-muted-foreground">
                      Showing {(movementPagination.page - 1) * movementPagination.limit + 1} to{" "}
                      {Math.min(movementPagination.page * movementPagination.limit, movementPagination.total)} of{" "}
                      {movementPagination.total} movements
                    </p>
                    <div className="flex space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={movementPagination.page <= 1}
                        onClick={() => setMovementPage((prev) => Math.max(1, prev - 1))}
                      >
                        Previous
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={movementPagination.page >= movementPagination.totalPages}
                        onClick={() =>
                          setMovementPage((prev) => Math.min(movementPagination.totalPages, prev + 1))
                        }
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                ) : null}
              </div>
            </TabsContent>

            <TabsContent value="shopify_push" className="space-y-4">
              <div className="flex flex-wrap items-center gap-3">
                <Select
                  value={pushStatus}
                  onValueChange={(value) => {
                    setPushStatus(value as PushStatusFilter | "all");
                    setPushPage(0);
                  }}
                >
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All statuses</SelectItem>
                    {pushStatusOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className={cn("w-[160px] justify-start text-left font-normal", !pushDateFrom && "text-muted-foreground")}>
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {pushDateFrom ? formatDate(pushDateFrom, "dd MMM yyyy") : "From date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={pushDateFrom}
                      onSelect={(date) => {
                        setPushDateFrom(date);
                        if (date && pushDateTo && date > pushDateTo) {
                          setPushDateTo(undefined);
                        }
                        setPushPage(0);
                      }}
                      disabled={(date) => date > new Date()}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
                <span className="text-muted-foreground text-sm">to</span>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className={cn("w-[160px] justify-start text-left font-normal", !pushDateTo && "text-muted-foreground")}>
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {pushDateTo ? formatDate(pushDateTo, "dd MMM yyyy") : "To date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={pushDateTo}
                      onSelect={(date) => {
                        setPushDateTo(date);
                        setPushPage(0);
                      }}
                      disabled={(date) => date > new Date() || (pushDateFrom ? date < pushDateFrom : false)}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>

                {pushHasActiveFilters && (
                  <Button variant="outline" onClick={clearPushFilters}>
                    Clear
                  </Button>
                )}

                <div className="ml-auto">
                  <Button
                    variant="outline"
                    disabled={isExporting || (pushHistoryQuery.data?.total ?? 0) === 0}
                    onClick={async () => {
                      const totalCount = pushHistoryQuery.data?.total ?? 0;
                      if (totalCount > EXPORT_ROW_CAP) {
                        toast({
                          title: `Too many rows to export (${totalCount.toLocaleString()})`,
                          description: `Push log currently has more than ${EXPORT_ROW_CAP.toLocaleString()} rows.`,
                          variant: "destructive",
                        });
                        return;
                      }
                      setIsExporting(true);
                      try {
                        // BLU-589: export honors the active on-screen filters.
                        const exportData = await trpcUtils.stockSync.exportPushHistory.fetch({
                          limit: EXPORT_ROW_CAP,
                          offset: 0,
                          status: pushStatus === "all" ? undefined : pushStatus,
                          fromDate: pushDateFrom ? toLocalDateString(pushDateFrom) : undefined,
                          toDate: pushDateTo ? toLocalDateString(pushDateTo) : undefined,
                        });
                        const csvRows: unknown[][] = [
                          ['Completed At', 'SKU', 'Location ID', 'Shopify Location ID', 'Previous Qty', 'Pushed Qty', 'Variants Updated', 'Variants Failed', 'Push Type', 'Triggered By', 'Error Details'],
                          ...exportData.items.map((row) => [
                            row.completed_at,
                            row.sku,
                            row.location_id,
                            row.shopify_location_id,
                            row.previous_qty ?? '',
                            row.pushed_qty,
                            row.variants_updated,
                            row.variants_failed,
                            row.push_type,
                            row.triggered_by,
                            row.error_details ? JSON.stringify(row.error_details) : '',
                          ]),
                        ];
                        downloadCsv(csvRows, `shopify-push-${formatDate(new Date(), 'yyyy-MM-dd')}.csv`);
                      } catch (err) {
                        toast({
                          title: "Export failed",
                          description: err instanceof Error ? err.message : "Failed to export push history",
                          variant: "destructive",
                        });
                      } finally {
                        setIsExporting(false);
                      }
                    }}
                    className="gap-1.5"
                  >
                    <Download className="h-4 w-4" />
                    {isExporting ? "Exporting..." : "Export CSV"}
                  </Button>
                </div>
              </div>
              <div className="bg-card rounded-lg shadow border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[180px]">Time</TableHead>
                      <TableHead>SKU</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Updated</TableHead>
                      <TableHead className="text-right">Failed</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Source</TableHead>
                      <TableHead>Error Details</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pushHistoryQuery.isLoading ? (
                      <TableRow>
                        <TableCell colSpan={8} className="py-10 text-center text-muted-foreground">
                          Loading push history...
                        </TableCell>
                      </TableRow>
                    ) : pushHistoryQuery.error ? (
                      <TableRow>
                        <TableCell colSpan={8} className="p-0">
                          <PageErrorState
                            title="Couldn't load push history"
                            description={pushHistoryQuery.error.message}
                            onRetry={() => pushHistoryQuery.refetch()}
                            retrying={pushHistoryQuery.isFetching}
                          />
                        </TableCell>
                      </TableRow>
                    ) : (pushHistoryQuery.data?.items ?? []).length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} className="p-0">
                          <EmptyState
                            icon={RefreshCw}
                            title={pushHasActiveFilters ? "No pushes match these filters" : "No push history yet"}
                            description={
                              pushHasActiveFilters
                                ? "Try a wider date range or a different status."
                                : "Pushes will appear here once inventory sync is active."
                            }
                          />
                        </TableCell>
                      </TableRow>
                    ) : (
                      (pushHistoryQuery.data?.items ?? []).map((item) => {
                        const { activated, errors: errorMsgs } = parsePushDetails(item.error_details);

                        return (
                          <TableRow key={item.id}>
                            <TableCell className="whitespace-nowrap text-muted-foreground">
                              {formatDistanceToNow(new Date(item.completed_at), { addSuffix: true })}
                            </TableCell>
                            <TableCell>
                              <Link
                                to={`/inventory?search=${encodeURIComponent(item.sku)}`}
                                className="font-mono text-sm text-primary hover:underline"
                              >
                                {item.sku}
                              </Link>
                            </TableCell>
                            <TableCell>
                              <div className="space-y-0.5">
                                {item.variants_failed === 0 && item.variants_updated > 0 ? (
                                  <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100 text-xs">Success</Badge>
                                ) : item.variants_updated > 0 && item.variants_failed > 0 ? (
                                  <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100 text-xs">Partial</Badge>
                                ) : item.variants_failed > 0 ? (
                                  <Badge className="bg-red-100 text-red-800 hover:bg-red-100 text-xs">Failed</Badge>
                                ) : (
                                  <Badge variant="outline" className="text-xs">No Changes</Badge>
                                )}
                                {activated > 0 && (
                                  <p className="text-xs text-blue-600">{activated} activated</p>
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="text-right tabular-nums">
                              {item.variants_updated > 0 ? (
                                <span className="text-emerald-600">{item.variants_updated}</span>
                              ) : '-'}
                            </TableCell>
                            <TableCell className="text-right tabular-nums">
                              {item.variants_failed > 0 ? (
                                <span className="text-red-600">
                                  <AlertCircle className="h-3 w-3 inline mr-0.5" />
                                  {item.variants_failed}
                                </span>
                              ) : '-'}
                            </TableCell>
                            <TableCell>
                              <Badge variant={item.push_type === 'PAP' ? 'default' : 'secondary'} className="text-xs">
                                {item.push_type}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              {item.triggered_by === 'dry_run' ? (
                                <Badge variant="outline" className="text-xs text-amber-600 border-amber-300">Dry Run</Badge>
                              ) : item.triggered_by === 'single_sku' ? (
                                <Badge variant="outline" className="text-xs">Single SKU</Badge>
                              ) : item.triggered_by === 'manual' ? (
                                <Badge variant="outline" className="text-xs">Manual</Badge>
                              ) : (
                                <Badge variant="outline" className="text-xs text-muted-foreground">Scheduled</Badge>
                              )}
                            </TableCell>
                            <TableCell className="max-w-[250px]">
                              {errorMsgs.length > 0 ? (
                                <p className="text-xs text-red-600 truncate" title={errorMsgs.join('; ')}>
                                  {errorMsgs[0]}
                                </p>
                              ) : '-'}
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>

                {(() => {
                  const total = pushHistoryQuery.data?.total ?? 0;
                  const pageSize = 50;
                  const totalPages = Math.ceil(total / pageSize);

                  return totalPages > 1 ? (
                    <div className="flex items-center justify-between px-4 py-3 border-t">
                      <p className="text-sm text-muted-foreground">
                        Showing {pushPage * pageSize + 1} to{" "}
                        {Math.min((pushPage + 1) * pageSize, total)} of{" "}
                        {total} pushes
                      </p>
                      <div className="flex space-x-2">
                        <Button variant="outline" size="sm" disabled={pushPage === 0} onClick={() => setPushPage(pushPage - 1)}>
                          Previous
                        </Button>
                        <Button variant="outline" size="sm" disabled={pushPage >= totalPages - 1} onClick={() => setPushPage(pushPage + 1)}>
                          Next
                        </Button>
                      </div>
                    </div>
                  ) : null;
                })()}
              </div>
            </TabsContent>

            <TabsContent value="production_events" className="space-y-4">
              <div className="flex flex-wrap items-center gap-3">
                <div className="relative w-full max-w-sm">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search by order name..."
                    value={prodEventSearch}
                    onChange={(event) => setProdEventSearch(event.target.value)}
                    className="pl-10"
                  />
                </div>
                <Select
                  value={prodEventType}
                  onValueChange={(value) => setProdEventType(value as ProductionEventType | "all")}
                >
                  <SelectTrigger className="w-[220px]">
                    <SelectValue placeholder="Event type" />
                  </SelectTrigger>
                  <SelectContent>
                    {prodEventTypeOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {clientFilterNode}
                <Button
                  variant="outline"
                  onClick={() => {
                    setProdEventSearch("");
                    setProdEventType("all");
                    setClientOrgFilter(ALL_CLIENTS);
                  }}
                >
                  Clear
                </Button>

                <div className="ml-auto">
                  <Button
                    variant="outline"
                    disabled={isExporting || (prodEventsQuery.data?.pagination?.total ?? 0) === 0}
                    onClick={async () => {
                      const totalCount = prodEventsQuery.data?.pagination?.total ?? 0;
                      if (totalCount > EXPORT_ROW_CAP) {
                        toast({
                          title: `Too many rows to export (${totalCount.toLocaleString()})`,
                          description: `Narrow filters to fewer than ${EXPORT_ROW_CAP.toLocaleString()} rows.`,
                          variant: "destructive",
                        });
                        return;
                      }
                      setIsExporting(true);
                      try {
                        const exportData = await trpcUtils.logs.exportProductionEvents.fetch({
                          locationId: selectedLocation?.id ?? undefined,
                          clientOrgId,
                          eventType: prodEventType === "all" ? undefined : prodEventType,
                          search: prodEventSearch.trim() || undefined,
                          page: 1,
                          limit: EXPORT_ROW_CAP,
                        });
                        const locName = selectedLocation?.name ?? '';
                        const csvRows: unknown[][] = [
                          ['Time', 'Event Type', 'Order', 'SKU', 'Client', 'Actor', 'Location', 'Metadata'],
                          ...exportData.data.map((row) => [
                            row.createdAt,
                            row.eventType,
                            row.orderName ?? '',
                            row.sku ?? '',
                            ownerOrgLabel(row.ownerOrganizationId, row.ownerOrganizationName),
                            row.actorName ?? '',
                            locName,
                            row.metadata ? JSON.stringify(row.metadata) : '',
                          ]),
                        ];
                        downloadCsv(csvRows, `production-events-${formatDate(new Date(), 'yyyy-MM-dd')}.csv`);
                      } catch (err) {
                        toast({
                          title: "Export failed",
                          description: err instanceof Error ? err.message : "Failed to export production events",
                          variant: "destructive",
                        });
                      } finally {
                        setIsExporting(false);
                      }
                    }}
                    className="gap-1.5"
                  >
                    <Download className="h-4 w-4" />
                    {isExporting ? "Exporting..." : "Export CSV"}
                  </Button>
                </div>
              </div>

              <div className="bg-card rounded-lg shadow border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[180px]">Time</TableHead>
                      <TableHead className="w-[180px]">Event Type</TableHead>
                      <TableHead>Order</TableHead>
                      <TableHead className="w-[180px]">SKU</TableHead>
                      {showClientFilter && <TableHead className="w-[160px]">Client</TableHead>}
                      <TableHead className="w-[160px]">Actor</TableHead>
                      <TableHead>Details</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {prodEventsQuery.isLoading ? (
                      <TableRow>
                        <TableCell colSpan={prodEventColSpan} className="py-10 text-center text-muted-foreground">
                          Loading production events...
                        </TableCell>
                      </TableRow>
                    ) : prodEventsQuery.error ? (
                      <TableRow>
                        <TableCell colSpan={prodEventColSpan} className="p-0">
                          <PageErrorState
                            title="Couldn't load production events"
                            description={prodEventsQuery.error.message}
                            onRetry={() => prodEventsQuery.refetch()}
                            retrying={prodEventsQuery.isFetching}
                          />
                        </TableCell>
                      </TableRow>
                    ) : (prodEventsQuery.data?.data ?? []).length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={prodEventColSpan} className="p-0">
                          <EmptyState
                            icon={Workflow}
                            title="No production events yet"
                            description="Production events for this location will appear here."
                          />
                        </TableCell>
                      </TableRow>
                    ) : (
                      (prodEventsQuery.data?.data ?? []).map((row) => (
                        <TableRow key={row.id}>
                          <TableCell className="whitespace-nowrap text-muted-foreground">
                            {formatDateTime(row.createdAt)}
                          </TableCell>
                          <TableCell>
                            <StatusBadge status={prodEventStatusStyle(row.eventType)} />
                          </TableCell>
                          <TableCell className="font-medium">
                            {row.orderName ?? "-"}
                          </TableCell>
                          <TableCell>
                            {row.sku ? (
                              <Link
                                to={`/inventory?search=${encodeURIComponent(row.sku)}`}
                                className="font-mono text-sm text-primary hover:underline"
                              >
                                {row.sku}
                              </Link>
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </TableCell>
                          {showClientFilter && (
                            <TableCell className="text-muted-foreground text-sm">
                              {ownerOrgLabel(row.ownerOrganizationId, row.ownerOrganizationName)}
                            </TableCell>
                          )}
                          <TableCell className="text-muted-foreground">
                            {row.actorName ?? "-"}
                          </TableCell>
                          <TableCell className="text-muted-foreground text-sm max-w-[300px] truncate" title={formatMetadata(row.metadata) ?? undefined}>
                            {formatMetadata(row.metadata) ?? "-"}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>

                {(() => {
                  const p = prodEventsQuery.data?.pagination;
                  if (!p || p.totalPages <= 1) return null;
                  return (
                    <div className="flex items-center justify-between px-4 py-3 border-t">
                      <p className="text-sm text-muted-foreground">
                        Showing {(p.page - 1) * p.limit + 1} to{" "}
                        {Math.min(p.page * p.limit, p.total)} of{" "}
                        {p.total} events
                      </p>
                      <div className="flex space-x-2">
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={p.page <= 1}
                          onClick={() => setProdEventPage((prev) => Math.max(1, prev - 1))}
                        >
                          Previous
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={p.page >= p.totalPages}
                          onClick={() => setProdEventPage((prev) => Math.min(p.totalPages, prev + 1))}
                        >
                          Next
                        </Button>
                      </div>
                    </div>
                  );
                })()}
              </div>
            </TabsContent>

            <TabsContent value="bulk_imports" className="space-y-4">
              <div className="flex flex-wrap items-center gap-3">
                {clientFilterNode}
                <div className="ml-auto">
                  <Button
                    variant="outline"
                    disabled={isExporting}
                    onClick={async () => {
                      setIsExporting(true);
                      try {
                        // Client filter applied server-side (RPC) — a naive
                        // batch-id .in() would overflow the PostgREST URL at
                        // this 10k export limit.
                        const exportData = await trpcUtils.logs.exportBulkImportBatches.fetch({
                          locationId: selectedLocation?.id ?? undefined,
                          clientOrgId,
                          page: 1,
                          limit: EXPORT_ROW_CAP,
                        });
                        if (exportData.pagination.total > EXPORT_ROW_CAP) {
                          toast({
                            title: `Too many rows to export (${exportData.pagination.total.toLocaleString()})`,
                            description: `Narrow by location to fewer than ${EXPORT_ROW_CAP.toLocaleString()} rows.`,
                            variant: "destructive",
                          });
                          return;
                        }
                        const csvRows: unknown[][] = [
                          ['Created At', 'Reference', 'Filename', 'Import Type', 'Status', 'Location', 'Clients', 'Created By', 'Item Count', 'Successful', 'Failed', 'Total Qty Change', 'Total Cost Change (USD)'],
                          ...exportData.data.map((row) => [
                            row.createdAt,
                            row.reference ?? '',
                            row.filename ?? '',
                            row.importType,
                            row.status,
                            row.locationName ?? (row.locationId === null ? 'All locations' : ''),
                            row.rowOrgs.map((o) => ownerOrgLabel(o.id, o.name)).join('; '),
                            row.createdByName ?? row.createdBy ?? '',
                            row.itemCount,
                            row.successfulCount,
                            row.failedCount,
                            row.totalQuantityChange,
                            row.totalCostChangeUsd ?? '',
                          ]),
                        ];
                        downloadCsv(csvRows, `adjustments-${formatDate(new Date(), 'yyyy-MM-dd')}.csv`);
                      } catch (err) {
                        toast({
                          title: "Export failed",
                          description: err instanceof Error ? err.message : "Failed to export bulk imports",
                          variant: "destructive",
                        });
                      } finally {
                        setIsExporting(false);
                      }
                    }}
                    className="gap-1.5"
                  >
                    <Download className="h-4 w-4" />
                    {isExporting ? "Exporting..." : "Export CSV"}
                  </Button>
                </div>
              </div>
              <BulkImportsTab
                organizationId={organization?.id ?? ""}
                locationId={selectedLocation?.id ?? undefined}
                clientOrgId={clientOrgId}
                showClientColumn={showClientFilter}
                ownerOrgLabel={ownerOrgLabel}
              />
            </TabsContent>

            <TabsContent value="receive_history" className="space-y-4">
              <div className="flex items-center">
                <div className="ml-auto">
                  <Button
                    variant="outline"
                    disabled={isExporting || !selectedLocation?.id}
                    title={!selectedLocation?.id ? 'Select a location first' : undefined}
                    onClick={async () => {
                      if (!selectedLocation?.id) return;
                      setIsExporting(true);
                      try {
                        const exportData = await trpcUtils.inventory.exportRecentReceipts.fetch({
                          locationId: selectedLocation.id,
                          limit: EXPORT_ROW_CAP,
                        });
                        const locName = selectedLocation.name;
                        const csvRows: unknown[][] = [
                          ['Received At', 'SKU', 'Product', 'Color', 'Size', 'Quantity Change', 'Location', 'Owner Org', 'Actor Org'],
                          ...exportData.receipts.map((row) => [
                            row.createdAt,
                            row.sku ?? '',
                            row.skuTitle ?? '',
                            row.skuColor ?? '',
                            row.skuSize ?? '',
                            row.quantityChange,
                            locName,
                            row.ownerOrganizationId,
                            row.actorOrganizationId ?? '',
                          ]),
                        ];
                        downloadCsv(csvRows, `receive-history-${formatDate(new Date(), 'yyyy-MM-dd')}.csv`);
                      } catch (err) {
                        toast({
                          title: "Export failed",
                          description: err instanceof Error ? err.message : "Failed to export receive history",
                          variant: "destructive",
                        });
                      } finally {
                        setIsExporting(false);
                      }
                    }}
                    className="gap-1.5"
                  >
                    <Download className="h-4 w-4" />
                    {isExporting ? "Exporting..." : "Export CSV"}
                  </Button>
                </div>
              </div>
              <ReceiveHistory embedded />
            </TabsContent>
          </Tabs>
        </main>
      </div>
      <BackToTopPill />
    </RoleGuard>
  );
};

export default LogsPage;
